import React, { useState, useEffect } from "react";
import { Alert, Button, Row, Col, Spinner } from "react-bootstrap";
import { checkEligibility } from "../../mediKreditapi";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById, updatePatient } from "../../api";

/** 🔹 Shared summary component for eligibility result */
export function EligibilitySummary({ eligibility }) {
  if (!eligibility?.eligibilityInfo) return null;

  const info = eligibility.eligibilityInfo;
  const patient = info.patient || {};
  const patientPlan = info.patientPlan || {};
  const patientRules = info.patientRules || [];
  const medicalAid = eligibility.medicalAidInfo || {};

  // The backend returns every code as a list (the switch sends one tag per
  // code); older stored results only carry the singular objects, so fall
  // back to wrapping those.
  const asList = (list, single) => {
    if (Array.isArray(list) && list.length > 0) return list;
    if (single && (single.code || single.description)) return [single];
    return [];
  };
  const warningList = asList(info.warningsList, info.warning);
  const fundWarningList = asList(info.fundWarningsList, info.fundWarning);
  const rejectionList = asList(info.rejectionsList, info.rejection);
  const fundRejectionList = asList(info.fundRejectionsList, info.fundRejection);

  const noMessages =
    warningList.length === 0 &&
    fundWarningList.length === 0 &&
    rejectionList.length === 0 &&
    fundRejectionList.length === 0;

  const formatRuleDate = (value) => {
    if (!value) return null;
    const s = String(value);
    if (/^\d{8}$/.test(s)) {
      // YYYYMMDD -> YYYY-MM-DD
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    }
    return s; // as-is for anything else
  };

  const joinDate = patientPlan.joined
    ? formatRuleDate(patientPlan.joined)
    : null;

  const planCode = patientPlan.plan || medicalAid.plan || null;

  const statusText = patient.statusDescr || "";
  const statusIsActive = statusText.toLowerCase().includes("active");

  return (
    <>
      {/* 🔹 Patient + Medical Aid header info */}
      <Row className="mt-2">
        {/* Patient side */}
        <Col md={6}>
          <p className="mb-0 fw-bold">
            {patient.fname || "-"} {patient.sname || ""}
          </p>

          {patient.id_nbr && (
            <p className="mb-0">ID Number: {patient.id_nbr}</p>
          )}

          {patient.dob && <p className="mb-0">DOB: {patient.dob}</p>}

          {patient.dep_cd && (
            <p className="mb-0">Dependant Code: {patient.dep_cd}</p>
          )}

          {statusText && (
            <p className="mb-0">
              Status:{" "}
              <span
                className={
                  statusIsActive ? "text-success fw-semibold" : "text-danger fw-semibold"
                }
              >
                {statusText}
              </span>
            </p>
          )}
        </Col>

        {/* Medical aid side */}
        <Col md={6}>
          <p className="mb-0 fw-bold">Medical Aid Details</p>

          {medicalAid.ch_id && (
            <p className="mb-0">
              Membership #: {medicalAid.ch_id}
            </p>
          )}

          {(medicalAid.sname || medicalAid.inits) && (
            <p className="mb-0">
              Main Member:{" "}
              {[medicalAid.inits, medicalAid.sname].filter(Boolean).join(" ")}
            </p>
          )}

          {planCode && (
            <p className="mb-0">Plan Code: {planCode}</p>
          )}

          {joinDate && (
            <p className="mb-0">Joined: {joinDate}</p>
          )}
        </Col>
      </Row>

      {/* 🔹 Status line (transaction result) */}
      <Row className="mt-2">
        <Col>
          <div
            className={
              info.status === "A"
                ? "text-success fw-bold"
                : "text-danger fw-bold"
            }
          >
            {info.status === "A"
              ? "Success"
              : info.status === "R"
              ? "Rejected"
              : info.status || "Unknown Status"}
          </div>
        </Col>
      </Row>

      {/* 🔹 HNET / TX number */}
      <Row className="mt-1">
        <Col>
          <p className="mb-0">
            HNET: {info.hnetNumber || "-"}
            <br />
            TXNR: {info.txNumber || "-"}
          </p>
        </Col>
      </Row>

      {/* 🔹 Plan Rules / Authorisations (if present) */}
      {patientRules.length > 0 && (
        <Row className="mt-3">
          <Col>
            <h6 className="mb-2 text-primary">Plan Rules / Authorisations</h6>
            {patientRules.map((rule, idx) => {
              const authInfo = rule.authInfo || {};
              const condition = rule.condition || {};
              const supply = rule.patientSupply || {};
              const r = rule.patientRule || {};
              const product = rule.product || {};
              const provider = rule.provider || {};
              const supplier = rule.supplier || {};

              const hasCondition =
                condition.condOti ||
                condition.condDiag ||
                condition.condStart ||
                condition.condEnd ||
                condition.condAuthNumber;

              const hasSupply =
                supply.refInd ||
                supply.extInd ||
                supply.extStart ||
                supply.extEnd ||
                supply.aclInd;

              const hasRule =
                r.qty || r.dsg || r.prdLimit;

              const hasProduct =
                product.prodType || product.prodDescr;

              const hasProvider =
                provider.provType || provider.provDescr;

              const hasSupplier =
                supplier.suppType || supplier.suppDescr;

              return (
                <div
                  key={idx}
                  className="border rounded p-2 mb-2 small bg-light"
                >
                  <div className="fw-semibold mb-1">
                    Rule {idx + 1}{" "}
                    {authInfo.auth_excl_indText && (
                      <span className="text-muted">
                        ({authInfo.auth_excl_indText})
                      </span>
                    )}
                  </div>

                  {hasCondition && (
                    <p className="mb-1">
                      <span className="fw-semibold">Condition: </span>
                      {condition.condDiag && (
                        <>Diag: {condition.condDiag} </>
                      )}
                      {condition.condOti && (
                        <>| OTI: {condition.condOti} </>
                      )}
                      {(condition.condStart || condition.condEnd) && (
                        <>
                          | Period:{" "}
                          {formatRuleDate(condition.condStart)} –{" "}
                          {formatRuleDate(condition.condEnd)}
                        </>
                      )}
                      {condition.condAuthNumber && (
                        <> | Auth #: {condition.condAuthNumber}</>
                      )}
                    </p>
                  )}

                  {hasSupply && (
                    <p className="mb-1">
                      <span className="fw-semibold">Supply: </span>
                      {supply.refInd && <>Ref: {supply.refInd} </>}
                      {supply.extInd && <>| Ext: {supply.extInd} </>}
                      {(supply.extStart || supply.extEnd) && (
                        <>
                          | Ext Period:{" "}
                          {formatRuleDate(supply.extStart)} –{" "}
                          {formatRuleDate(supply.extEnd)}
                        </>
                      )}
                      {supply.aclInd && <> | ACL: {supply.aclInd}</>}
                    </p>
                  )}

                  {hasRule && (
                    <p className="mb-1">
                      <span className="fw-semibold">Rule: </span>
                      {r.qty && <>Qty: {r.qty} </>}
                      {r.dsg && <>| Dose: {r.dsg} </>}
                      {r.prdLimit && <>| Limit: {r.prdLimit}</>}
                    </p>
                  )}

                  {hasProduct && (
                    <p className="mb-1">
                      <span className="fw-semibold">Product: </span>
                      {product.prodType && <>Type: {product.prodType} </>}
                      {product.prodDescr && <>| {product.prodDescr}</>}
                    </p>
                  )}

                  {hasProvider && (
                    <p className="mb-1">
                      <span className="fw-semibold">Provider: </span>
                      {provider.provType && <>Type: {provider.provType} </>}
                      {provider.provDescr && <>| {provider.provDescr}</>}
                    </p>
                  )}

                  {hasSupplier && (
                    <p className="mb-0">
                      <span className="fw-semibold">Supplier: </span>
                      {supplier.suppType && <>Type: {supplier.suppType} </>}
                      {supplier.suppDescr && <>| {supplier.suppDescr}</>}
                    </p>
                  )}
                </div>
              );
            })}
          </Col>
        </Row>
      )}

      {/* 🔹 Warnings / rejections (if any) */}
      <Row className="mt-1">
        <Col>
          {warningList.map((w, i) => (
            <p className="text-secondary mb-1" key={`warn-${i}`}>
              {[w.code, w.description].filter(Boolean).join(" - ")}
            </p>
          ))}

          {fundWarningList.map((w, i) => (
            <p className="text-secondary mb-1" key={`fwarn-${i}`}>
              Medical Aid: {[w.code, w.description].filter(Boolean).join(" - ")}
            </p>
          ))}

          {rejectionList.map((r, i) => (
            <p className="text-danger mb-1" key={`rej-${i}`}>
              {[r.code, r.description].filter(Boolean).join(" - ")}
            </p>
          ))}

          {fundRejectionList.map((r, i) => (
            <p className="text-danger mb-1" key={`frej-${i}`}>
              Medical Aid: {[r.code, r.description].filter(Boolean).join(" - ")}
            </p>
          ))}

          {noMessages && (
            <p className="fw-bold text-success mt-1 mb-1">
              Check Successful
            </p>
          )}
        </Col>
      </Row>
    </>
  );
}



export default function CheckEligibility({ patient, account, onEligibility }) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [eligibilityResponse, setEligibilityResponse] = useState(null);
  const [practice, setPractice] = useState(null);

  // When a parent handles the result (onEligibility), this component renders
  // only the button and reports outcomes upward, including failures as
  // { error }. Rendering the alert and summary inline here used to blow up
  // the parent's header row, which laid the button out in a flex line next
  // to its heading.
  const reportsUpward = typeof onEligibility === "function";

  useEffect(() => {
    async function fetchData() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");

        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPractice(practices[0]);
      } catch (error) {
        console.error("❌ Error loading practice data:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const medicalAidInfo = {
    plan: account?.accountMediKreditPlanNumber04,
    ch_id: account?.accountMedicalAidNumber,
    sname: account?.accountLastName,
    inits: account?.accountInitials,
  };

  const practiceInfo = {
    sp_bhf: practice?.practiceNumber,
    sp_hpc: practice?.hpcsaNumber,
    grp_prac: practice?.practiceNumber,
  };

  const patientInfo = {
    dep_cd: patient.patientMedicalAidDependantCode,
    dob: patient.patientDOB,
    id_nbr: patient.patientIdNumber,
    fname: patient.patientFirstName,
    sname: patient.patientLastName,
    pinits: patient.patientInitials,
    gend: patient.patientGender,
    rlnship: "",
    status: patient.patientStatus,
    accountNumber: account?.accountNumber || "",
  };

  async function Eligibility(e) {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await checkEligibility(
        practiceInfo,
        medicalAidInfo,
        patientInfo
      );

      setEligibilityResponse(response);
      if (reportsUpward) {
        onEligibility(response);
      }

      // 🔹 If check was successful, update the patient in DB
      const info = response?.eligibilityInfo;
      if (info?.status === "A" && patient?._id) {
        const nowIso = new Date().toISOString();

        const statusDescr =
          info.patient?.statusDescr || patient.patientMedicalAidStatus || "Active";

        const joinedDate =
          info.patientPlan?.joined || patient.patientMedicalAidJoinDate || null;

        try {
          await updatePatient(patient._id, {
            patientMedicalAidStatus: statusDescr,
            patientMedicalAidJoinDate: joinedDate,
            patientMedicalAidLastCheck: nowIso,
          });
        } catch (updateErr) {
          console.error("❌ Failed to update patient after eligibility:", updateErr);
        }
      }
    } catch (error) {
      console.error("❌ Eligibility error:", error);
      // Show the server's own message when there is one (a transient outage
      // carries the MediKredit trace ID support asks for); anything else
      // gets a plain retry message instead of silence.
      const serverMsg = error.response?.data?.error;
      const traceId = error.response?.data?.medikreditTraceId;
      const message = serverMsg
        ? traceId
          ? `${serverMsg} (Ref: ${traceId})`
          : serverMsg
        : "The eligibility check could not be completed. Please try again.";
      setErrorMessage(message);
      if (reportsUpward) {
        onEligibility({ error: message });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Row>
        <Button
          className="ms-2 mt-0 me-2"
          style={{ width: "175px" }}
          onClick={Eligibility}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Loading...
            </>
          ) : (
            <>
              <i className="fa-solid fa-magnifying-glass me-2"></i>
              Check Status
            </>
          )}
        </Button>
      </Row>

      {/* Standalone fallback only: with an onEligibility handler the parent
          owns the error and result rendering, so nothing may render here or
          it lands inside the parent's header layout. */}
      {!reportsUpward && errorMessage && (
        <Row className="mt-2">
          <Col md={12}>
            <Alert
              variant="danger"
              className="d-flex justify-content-start text-center mb-2"
            >
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {!reportsUpward && (
        <EligibilitySummary eligibility={eligibilityResponse} />
      )}
    </>
  );
}
